interface SavingsGoalInput {
  goalAmount: number
  currentSavings: number
  targetDate: string // ISO date
  expectedReturn: number // annual %
}

export function calculateSavingsGoal(input: SavingsGoalInput) {
  const now = new Date()
  const target = new Date(input.targetDate)
  const totalMonths = Math.max(1,
    (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth())
  )

  const r = input.expectedReturn / 100 / 12
  const fv = input.goalAmount
  const pv = input.currentSavings

  // Solve for PMT: FV = PV*(1+r)^n + PMT*((1+r)^n - 1)/r
  // PMT = (FV - PV*(1+r)^n) * r / ((1+r)^n - 1)
  let monthlyNeeded: number
  if (r > 0) {
    const compound = Math.pow(1 + r, totalMonths)
    const futureValueOfCurrent = pv * compound
    const remaining = fv - futureValueOfCurrent
    monthlyNeeded = remaining > 0 ? remaining * r / (compound - 1) : 0
  } else {
    monthlyNeeded = Math.max(0, (fv - pv) / totalMonths)
  }

  // Generate projection
  const projection = []
  let balance = pv
  let totalContributions = pv
  for (let month = 1; month <= totalMonths; month++) {
    const growth = balance * r
    balance += growth + monthlyNeeded
    totalContributions += monthlyNeeded

    if (month % Math.max(1, Math.floor(totalMonths / 24)) === 0 || month === totalMonths || month === 1) {
      projection.push({
        month,
        balance: Math.round(balance),
        contributions: Math.round(totalContributions),
        growth: Math.round(balance - totalContributions),
        goalLine: fv,
      })
    }
  }

  const totalContributed = pv + monthlyNeeded * totalMonths
  const totalGrowth = fv - totalContributed

  return {
    monthlyNeeded: Math.round(monthlyNeeded * 100) / 100,
    weeklyNeeded: Math.round((monthlyNeeded * 12 / 52) * 100) / 100,
    totalMonths,
    totalContributed: Math.round(totalContributed),
    totalGrowth: Math.round(Math.max(0, totalGrowth)),
    goalAmount: fv,
    currentSavings: pv,
    gap: Math.round(Math.max(0, fv - pv)),
    progress: fv > 0 ? Math.round((pv / fv) * 1000) / 10 : 100,
    projection,
  }
}
